/**
 * THE STATUS BAR — the story everyone in the house sees.
 *
 * Four words, in order: sent · accepted · on the way · delivered. Nothing
 * here decides anything; it polls the server and shows what came back. The
 * detail underneath is the SHARED components/shared/ActionTimeline, the same
 * one the Command Center uses, so there is one timeline in the product.
 *
 * A member who cannot approve sees "Waiting for <name>" and no PIN, ever.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2, Clock, Truck, PackageCheck, ChevronDown, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import ActionTimeline from '../shared/ActionTimeline';
import GroceryTextPreview from './GroceryTextPreview';

const API = (typeof window !== 'undefined' ? window.location.origin : '');
const POLL_MS = 5000;
const STEPS = ['sent', 'accepted', 'on_the_way', 'delivered'];
const ICONS = { sent: Check, accepted: PackageCheck, on_the_way: Truck, delivered: Check };

const call = (path, session, body) => fetch(`${API}/api/grocery${path}`, {
    method: body ? 'POST' : 'GET',
    headers: { 'Content-Type': 'application/json', 'X-Grocery-Session': session },
    body: body ? JSON.stringify(body) : undefined,
}).then(async (r) => {
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw Object.assign(new Error('http'), { status: r.status, data });
    return data;
});

export default function GroceryOrderStatus({ session, memberId, canApprove, isDemo,
    onChanged, onReading, frost = '' }) {
    const { t, i18n } = useTranslation();
    const [order, setOrder] = useState(null);
    const [open, setOpen] = useState(false);
    const [pin, setPin] = useState('');
    const [busy, setBusy] = useState(false);
    const [problem, setProblem] = useState(null);

    const load = useCallback(() => {
        call('/orders', session)
            .then((d) => {
                const live = (d.orders || []).find((o) => o.status !== 'cancelled');
                setOrder(live || null);
            })
            .catch(() => { /* keep the last good one */ });
    }, [session]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        const id = setInterval(load, POLL_MS);
        return () => clearInterval(id);
    }, [load]);

    const approve = async (code) => {
        setBusy(true);
        try {
            await call(`/orders/${order.id}/approve`, session, { member_id: memberId, pin: code });
            setPin(''); setProblem(null);
            toast.success(t('groceryStatus.approved'));
            load(); onChanged?.();
        } catch (e) {
            const d = e.data?.detail;
            const code2 = typeof d === 'object' ? d.code : d;
            if (e.status === 429 || code2 === 'pin_locked') {
                setProblem(t('groceryOrder.pinLocked', { minutes: d?.minutes || 15 }));
            } else if (code2 === 'pin_invalid') {
                setProblem(t('groceryOrder.pinWrong', { left: d?.tries_left ?? 0 }));
            } else {
                setProblem(t('groceryOrder.loadFailed'));
            }
            setPin('');
        } finally { setBusy(false); }
    };

    const cancel = async () => {
        setBusy(true);
        try {
            await call(`/orders/${order.id}/cancel`, session, { member_id: memberId });
            toast.success(t('groceryStatus.cancelled'));
            load(); onChanged?.();
        } catch (_e) { toast.error(t('groceryOrder.err.too_late_to_change')); }
        finally { setBusy(false); }
    };

    const timeline = useCallback(() => Promise.resolve({
        steps: (order?.history || []).map((h) => ({
            step: h.status, at: h.at, actor_name: h.by_name, notes: h.note,
        })),
    }), [order]);

    if (!order) return null;

    const waiting = order.status === 'pending_approval';
    const at = STEPS.indexOf(order.step);
    const when = order.window
        ? `${new Date(`${order.window.day}T12:00:00`).toLocaleDateString(i18n.language, {
            weekday: 'short', day: 'numeric', month: 'short' })} · ${order.window.start}–${order.window.end}`
        : '';

    return (
        <div className={`rounded-2xl border-2 border-emerald-200 bg-emerald-50/60 p-4 dark:border-emerald-500/30 dark:bg-emerald-500/5${frost}`}
            data-testid="grocery-order-status">
            {/* HOW THE GROCER WILL BILL IT, in one line, as it was when sent. */}
            <p className="mb-2 text-xs font-semibold text-slate-600 dark:text-slate-300"
                data-testid="grocery-order-billing">
                {order.billing_mode === 'separate_bills'
                    ? t('grocery.billSummarySeparate',
                        { names: (order.billing_names || []).join(', ') })
                    : t('grocery.billSummaryOne', { owner: order.billing_owner_name || '' })}
            </p>
            {waiting ? (
                <div className="space-y-3" data-testid="grocery-order-waiting">
                    <p className="flex items-center gap-2 text-base font-bold text-[#13265C] dark:text-white">
                        <Clock className="h-5 w-5 text-[#F58220]" />
                        {t('groceryOrder.waitingTitle', { name: order.approver_name || '' })}
                    </p>
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                        {t('groceryOrder.waitingBody', { name: order.approver_name || '' })}
                    </p>
                    {canApprove ? (
                        <div className="space-y-2" data-testid="grocery-approve-box">
                            <p className="text-sm font-semibold">
                                {t('groceryStatus.approveAsk', { name: order.placed_by_name || '' })}
                            </p>
                            <input value={pin} inputMode="numeric" type="password" maxLength={4}
                                onChange={(e) => {
                                    const v = e.target.value.replace(/\D/g, '').slice(0, 4);
                                    setPin(v); setProblem(null);
                                    if (v.length === 4) approve(v);
                                }}
                                data-testid="grocery-approve-pin" data-launcher-avoid="lift"
                                className="min-h-[56px] w-full rounded-2xl border-2 px-4 text-center text-2xl tracking-[0.5em] dark:border-slate-700 dark:bg-slate-900" />
                            {isDemo ? (
                                <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-300"
                                    data-testid="grocery-approve-demo-hint">{t('groceryOrder.demoAnyCode')}</p>
                            ) : null}
                            {problem ? <p className="text-sm font-semibold text-rose-600"
                                data-testid="grocery-approve-error">{problem}</p> : null}
                        </div>
                    ) : null}
                </div>
            ) : (
                <>
                    <div className="flex items-center justify-between gap-2">
                        <p className="text-base font-bold" data-testid="grocery-order-step-label">
                            {t(`groceryStatus.step.${order.step}`)}
                        </p>
                        <span className="text-xs font-semibold text-slate-500" data-testid="grocery-order-when">{when}</span>
                    </div>
                    <ol className="mt-3 flex items-center gap-1" data-testid="grocery-order-track">
                        {STEPS.map((s, i) => {
                            const Icon = ICONS[s];
                            const done = i <= at;
                            return (
                                <li key={s} className="flex min-w-0 flex-1 flex-col items-center gap-1"
                                    data-testid={`grocery-status-${s}`} data-state={done ? 'done' : 'todo'}>
                                    <span className={`flex h-9 w-9 items-center justify-center rounded-full ${
                                        done ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-400 dark:bg-slate-800'}`}>
                                        {i === at && i < 3 ? <Loader2 className="h-4 w-4 animate-spin" />
                                            : <Icon className="h-4 w-4" />}
                                    </span>
                                    <span className={`text-center text-[11px] leading-tight ${
                                        done ? 'font-bold text-emerald-800 dark:text-emerald-300' : 'text-slate-400'}`}>
                                        {t(`groceryStatus.step.${s}`)}
                                    </span>
                                </li>
                            );
                        })}
                    </ol>
                    {order.status === 'delayed' ? (
                        <p className="mt-2 inline-flex rounded-full bg-[#F58220] px-3 py-1 text-sm font-bold text-[#13265C]" data-testid="grocery-order-delayed">
                            {t('groceryStatus.delayed')}
                        </p>
                    ) : null}
                </>
            )}

            <button onClick={() => { setOpen((v) => !v); onReading?.(!open); }}
                data-testid="grocery-order-detail-btn"
                className="mt-3 flex min-h-[44px] items-center gap-1 text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                {t('groceryStatus.detail')}
                <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            {open ? (
                <div className="mt-2 space-y-4" data-testid="grocery-order-detail">
                    <ActionTimeline fetcher={timeline} testid="grocery-order-timeline"
                        labels={{
                            title: t('groceryStatus.whatHappened'), loading: t('groceryStatus.loading'),
                            empty: t('groceryStatus.nothingYet'), by: t('groceryStatus.by'),
                            noActor: t('groceryStatus.byStore'),
                            step: t('groceryStatus.history', { returnObjects: true }),
                        }} />
                    <GroceryTextPreview session={session} side="shopper" />
                    {order.can_cancel ? (
                        <Button variant="outline" onClick={cancel} disabled={busy}
                            data-testid="grocery-order-cancel" data-launcher-avoid="lift"
                            className="min-h-[44px] w-full gap-2 border-rose-300 text-rose-600">
                            <XCircle className="h-4 w-4" />{t('groceryStatus.cancel')}
                        </Button>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}
